
export function useLRUCache<T>(capacity: number) {
  let cache: { [key: string]: T } = Object.create(null);
  let keys: string[] = [];

  function has (key: string) {
    return cache[key] !== undefined;
  }

  return {
    /**
     * Get the value associated with the given key from the cache.
     * @param key - The key to look up in the cache.
     * @returns The value associated with the key, or undefined if the key is not in the cache.
     */
    get(key: string): T | undefined {
      if (has(key)) {
          // Move the accessed key to the end of the keys array (most recently used)
          keys.splice(keys.indexOf(key), 1);
          keys.push(key);
          return cache[key];
      }
      return undefined;
    },
    /**
     * Put a new key-value pair into the cache.
     * @param key - The key to insert into the cache.
     * @param value - The value associated with the key.
     * @param scope - The scope of the key.
     */
    put(key: string, value: T, scope: string): void {
      if (has(key)) {
          // Update the existing key's value and move it to the end of the keys array
          cache[key] = value;
          keys.splice(keys.indexOf(key), 1);
          keys.push(key);
      } else {
          // Add a new key-value pair to the cache
          if (keys.length >= capacity) {
              // If the cache is full, remove the least recently used key
              delete cache[keys.shift()!];
          }``
          cache[key] = value;
          keys.push(key);
      }
    },
    has,
    get cache () {
      return {
        cache,
        keys
      }
    },
    set cache (data: { cache: { [key: string]: any }, keys: string[] }) {
      cache = data.cache;
      keys = data.keys;
    }
  }
}

function usePersistenceLRUCache<T> (capacity: number, storage: {
  getItem: (key: string) => string | null,
  setItem: (key: string, value: string) => void,
  removeItem: (key: string) => void
}, scope: string, maxAge?: number) {
  const _scope = scope || 'default';
  const key = `KSO_LRUCACHE_${_scope}`;
  const _maxAge = maxAge || 0;
  const data = storage.getItem(key);
  const LRU = useLRUCache(capacity)
  if (data) {
      const { cache, keys, expireDate } = JSON.parse(data);
      if (expireDate && Date.now() >= expireDate) {
        storage.removeItem(key);
      } else {
        LRU.cache = { cache, keys }
      }
  }

  function save () {
    // Save cache data to persistence storage
    storage.setItem(key, JSON.stringify(Object.assign({}, LRU.cache, { expireDate: _maxAge ? Date.now() + _maxAge : 0 })));
  }


  function get (key: string) {
    const value = LRU.get(key);
    if (value !== undefined) {
      save();
    }
    return value
  }

  function put(key: string, value: T, scope: string): void {
    LRU.put(key, value, scope);
    save();
  }

  return {
    has: LRU.has,
    get,
    put
  }
  
}

export function useLocalStorageLRUCache<T> (capacity: number, scope: string, maxAge?: number) {
  return usePersistenceLRUCache<T>(capacity, window.localStorage, scope, maxAge)
}

export function useSessionStorageLRUCache<T> (capacity: number, scope: string, maxAge?: number) {
  return usePersistenceLRUCache<T>(capacity, window.sessionStorage, scope, maxAge)
}

